// useSandboxStore — returns scenario CRUD functions that are sandbox-aware.
// During impersonation, all operations go through the in-memory sandbox.
// Outside impersonation, they delegate to the real scenarioStore.

import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  loadScenarios,
  loadDrafts,
  createScenario,
  updateScenario,
  renameScenario,
  deleteScenario,
  duplicateScenario,
  saveDraftScenario,
  updateDraftScenario,
  finalizeDraft,
  reorderScenarios,
} from '@/tax/scenarioStore';
import type { TaxScenario, TaxScenarioInput } from '@/tax/types';

export function useSandboxStore() {
  const { isImpersonating, sandbox } = useImpersonation();

  if (isImpersonating && sandbox) {
    return {
      isSandbox: true as const,
      sandboxVersion: sandbox.version,
      loadScenarios: async (): Promise<TaxScenario[]> => sandbox.loadScenarios(),
      loadDrafts: async (): Promise<TaxScenario[]> => sandbox.loadDrafts(),
      createScenario: sandbox.createScenario,
      updateScenario: sandbox.updateScenario,
      // Sandbox rename reuses the in-memory update — the recalculation it does
      // is harmless (name doesn't affect math) and nothing touches the DB.
      renameScenario: (id: string, input: TaxScenarioInput): Promise<TaxScenario> =>
        sandbox.updateScenario(id, input),
      deleteScenario: async (id: string): Promise<void> => { sandbox.deleteScenario(id); },
      duplicateScenario: sandbox.duplicateScenario,
      saveDraftScenario: sandbox.saveDraft,
      updateDraftScenario: sandbox.updateDraft,
      finalizeDraft: sandbox.finalizeDraft,
      reorderScenarios: async (ids: string[], orders: number[]): Promise<void> => { sandbox.reorderScenarios(ids, orders); },
    };
  }

  return {
    isSandbox: false as const,
    sandboxVersion: -1,
    loadScenarios,
    loadDrafts,
    createScenario,
    updateScenario,
    renameScenario,
    deleteScenario,
    duplicateScenario,
    saveDraftScenario,
    updateDraftScenario,
    finalizeDraft,
    reorderScenarios,
  };
}
